import { useQuery } from '@tanstack/react-query';

// common
import { KEYS_QUERY } from 'common/constants';
import useService from 'common/hook/useService';
import { handleException } from 'modules/account/detail/helpers/utils';
import { SupplierFilterModel } from 'modules/suppliers/types';

// service
import type { SupplierResponse } from 'modules/suppliers/services/useProcessingFoodSuppliers';

interface Queries extends Partial<Omit<SupplierFilterModel, 'name'>> {
  offset: number;
  pageSize: number;
  sort?: string;
}

interface Props {
  queries: Queries;
}

const useUnprocessedDishSuppliers = ({ queries }: Props) => {
  const service = useService();

  const queryKeys = Object.keys(queries).reduce((acc, next) => {
    return queries[next] === null || queries[next] === undefined ? acc : [...acc, queries[next]];
  }, [] as string[]);

  return useQuery({
    queryKey: [KEYS_QUERY.unprocessedDishSupplier, ...queryKeys],
    queryFn: async () => {
      try {
        if (!service || !queries.customerOperationCode) return { results: [], total: 0 };
        const raw = await service.SupplierProfileController_listSupplierProfiles({
          queries: {
            status: queries.status || 'enabled',
            classificationGroup: 'packed-dish',
            pageIndex: queries.offset ?? 0,
            pageSize: queries.pageSize || 20,
            sorts: queries.sort || 'commodityGroup',
          },
          params: {
            customerOperationCode: queries.customerOperationCode || '',
          },
        });

        let res: SupplierResponse;
        if (Array.isArray(raw)) {
          res = { results: raw, total: raw.length };
        } else if (
          raw &&
          typeof raw === 'object' &&
          'data' in raw &&
          raw.data &&
          typeof raw.data === 'object' &&
          'results' in (raw.data as object)
        ) {
          res = raw.data as SupplierResponse;
        } else {
          res = raw as SupplierResponse;
        }
        return res;
      } catch (error) {
        console.error('error: ', error);
        handleException(error);
        return { results: [], total: 0 };
      }
    },
    enabled: !!service && !!queries.customerOperationCode,
  });
};

export default useUnprocessedDishSuppliers;
